<template>
  <div class="apw">
    <transition name="apw-fade">
      <div v-show="open" class="apw-panel">
        <ask-peter-scoped ref="chat" :brand-name="brandName"
          :user-selection="userSelection" :user-details="userDetails" />
      </div>
    </transition>

    <!-- First-visit greeting, next to the mascot. Goes away on its own or on click. -->
    <transition name="apw-fade">
      <div v-if="greeting && !open" class="apw-greeting" @click="toggle">Hi, I'm {{ mascotName }} 👋 Ask me about the product data</div>
    </transition>

    <!-- The mascot IS the launcher: a friendly assistant that waves; when the panel is open it shrinks
         into the close button. Inline SVG so it can be animated with CSS and needs no asset.
         Two mascots share the same classes (eyes blink, hand waves, face hides behind the cross). -->
    <button class="apw-launcher" :class="{ 'apw-open': open, 'apw-wave': waving }" @click="toggle"
      @mouseenter="wave" :aria-label="open ? 'Close ' + brandName : 'Ask ' + brandName"
      :title="'Ask ' + mascotName + ' about the product data'">

      <!-- Hugo: a hedgehog. Spines are the protection; glasses and bow tie say he knows his data. -->
      <svg v-if="mascot === 'hugo'" class="apw-mascot" viewBox="0 -12 96 108" aria-hidden="true">
        <g class="apw-face">
          <!-- spines -->
          <!-- a rounded mound of spines BEHIND the head (the head overlaps it), so they read as the back of a
               hedgehog rather than a crown on the forehead -->
          <path d="M18 64 Q8 54 16 44 Q12 32 24 32 Q24 20 36 24 Q42 12 50 22 Q60 12 64 24 Q76 20 76 32 Q88 32 84 44 Q92 54 80 64 Z" fill="#1E2A78" />
          <path d="M22 58 Q16 50 22 44 Q20 36 30 36 Q30 26 40 30 Q44 20 50 28 Q58 20 62 30 Q72 26 72 36 Q82 36 78 44 Q84 50 76 58 Z" fill="#2B3A9E" />
          <!-- head -->
          <ellipse cx="48" cy="60" rx="27" ry="21" fill="#F3D9B1" />
          <!-- ears -->
          <circle cx="27" cy="46" r="5" fill="#F3D9B1" /><circle cx="27" cy="46" r="2.5" fill="#E2B58E" />
          <circle cx="69" cy="46" r="5" fill="#F3D9B1" /><circle cx="69" cy="46" r="2.5" fill="#E2B58E" />
          <!-- eyes (blink) -->
          <g class="apw-eyes">
            <circle cx="40" cy="54" r="5.5" fill="#fff" />
            <circle cx="56" cy="54" r="5.5" fill="#fff" />
            <circle cx="41.5" cy="55" r="2.6" fill="#2B1A16" />
            <circle cx="57.5" cy="55" r="2.6" fill="#2B1A16" />
          </g>
          <!-- round glasses, dark rims on the pale face -->
          <g class="apw-glasses" stroke="#1E2A78" stroke-width="2.2" fill="none" stroke-linecap="round">
            <circle cx="40" cy="54" r="8" /><circle cx="56" cy="54" r="8" />
            <line x1="47.5" y1="53" x2="48.5" y2="53" />
            <line x1="32" y1="52" x2="27" y2="50" /><line x1="64" y1="52" x2="69" y2="50" />
          </g>
          <!-- nose + smile -->
          <ellipse cx="48" cy="66" rx="3.2" ry="2.4" fill="#2B1A16" />
          <path class="apw-smile" d="M44 70 Q48 74 52 70" stroke="#2B1A16" stroke-width="2.4" fill="none" stroke-linecap="round" />
          <!-- bow tie -->
          <path d="M40 80 L48 83 L56 80 L56 88 L48 85 L40 88 Z" fill="#00A76F" /><circle cx="48" cy="84" r="1.8" fill="#1E2A78" />
          <!-- waving paw -->
          <g class="apw-hand">
            <line x1="80" y1="64" x2="88" y2="48" stroke="#F3D9B1" stroke-width="6" stroke-linecap="round" />
            <circle cx="89" cy="44" r="7" fill="#F3D9B1" stroke="#1E2A78" stroke-width="1.6" />
          </g>
        </g>
        <!-- closed state: a plain navy disc with a cross -->
        <g class="apw-close" fill="#1E2A78" stroke="#fff" stroke-width="5" stroke-linecap="round">
          <circle cx="48" cy="48" r="30" stroke="none" />
          <line x1="36" y1="36" x2="60" y2="60" /><line x1="60" y1="36" x2="36" y2="60" />
        </g>
      </svg>

      <!-- Peter: the graduate assistant. -->
      <svg v-else class="apw-mascot" viewBox="0 -12 96 108" aria-hidden="true">
        <!-- head -->
        <rect x="16" y="18" width="64" height="56" rx="20" fill="#1E2A78" />
        <rect x="24" y="26" width="48" height="40" rx="14" fill="#3B6FE3" />
        <g class="apw-face">
          <!-- graduation cap, seen straight on: flat board, band on the head, tassel hanging on the left,
               away from the waving hand, in the classic gold. -->
          <g class="apw-cap">
            <rect x="24" y="10" width="48" height="12" rx="6" fill="#2B3A9E" />
            <polygon points="48,-4 90,10 48,24 6,10" fill="#1E2A78" stroke="#fff" stroke-width="1.6" stroke-linejoin="round" />
            <path d="M48 10 L48 4 Q24 6 18 24 L16 34" stroke="#F2B233" stroke-width="2.4" fill="none" stroke-linecap="round" />
            <path d="M16 34 L12 44 L20 44 Z" fill="#F2B233" />
          </g>
          <!-- eyes (blink) -->
          <g class="apw-eyes">
            <circle cx="38" cy="44" r="6" fill="#fff" />
            <circle cx="58" cy="44" r="6" fill="#fff" />
            <circle cx="39.5" cy="45" r="2.8" fill="#1E2A78" />
            <circle cx="59.5" cy="45" r="2.8" fill="#1E2A78" />
          </g>
          <!-- round glasses: thin white rims, a bridge, and short temples into the head -->
          <g class="apw-glasses" stroke="#fff" stroke-width="2.2" fill="none" stroke-linecap="round">
            <circle cx="38" cy="44" r="8.5" />
            <circle cx="58" cy="44" r="8.5" />
            <line x1="47.5" y1="43" x2="48.5" y2="43" />
            <line x1="29.5" y1="42" x2="25" y2="40" /><line x1="66.5" y1="42" x2="71" y2="40" />
          </g>
          <!-- smile -->
          <path class="apw-smile" d="M43 60 Q48 65 53 60" stroke="#fff" stroke-width="3" fill="none" stroke-linecap="round" />
          <!-- waving hand -->
          <g class="apw-hand">
            <line x1="80" y1="60" x2="88" y2="44" stroke="#1E2A78" stroke-width="6" stroke-linecap="round" />
            <circle cx="89" cy="40" r="7" fill="#00A76F" />
          </g>
        </g>
        <!-- close cross, only when open -->
        <g class="apw-close" stroke="#fff" stroke-width="5" stroke-linecap="round">
          <line x1="36" y1="36" x2="60" y2="60" /><line x1="60" y1="36" x2="36" y2="60" />
        </g>
      </svg>
    </button>
  </div>
</template>

<script>
import AskPeterScoped from './AskPeterScoped.vue';

// Floating launcher + panel for the data-coverage chat. Mounted once in the app shell so it floats on
// every screen. Runs inside the authenticated session, so the /converse call authenticates by cookie.
// The launcher is a small mascot (inline SVG) that waves on first sight and on hover; the panel is
// hidden, not destroyed, while minimised so the conversation survives.
//
// Two mascots exist so the business can compare them: "peter" (default, the graduate) and "hugo"
// (the hedgehog). Pick one with ?mascot=hugo or ?mascot=peter in the URL once; the choice is kept in
// localStorage for that browser. Not a product setting - a demo switch. ?greet=1 replays the greeting.
const MASCOTS = {
  peter: { name: 'Peter', brand: 'Peter Data Pro' },
  hugo: { name: 'Hugo', brand: 'Hugo' },
};

export default {
  name: 'AskPeterWidget',
  components: { AskPeterScoped },
  props: ['userSelection', 'userDetails'],
  data: function () {
    return { open: false, waving: false, greeting: false, waveTimer: null, greetTimer: null, mascot: 'peter' };
  },
  computed: {
    mascotName: function () { return MASCOTS[this.mascot].name; },
    brandName: function () { return MASCOTS[this.mascot].brand; },
  },
  created: function () {
    try {
      var params = new URLSearchParams(window.location.search);
      var wanted = params.get('mascot');
      // ?greet=1 replays the first-visit greeting (and the chat's rocket) - handy for demos and videos.
      if (params.has('greet')) { window.sessionStorage.removeItem('apw-greeted'); window.sessionStorage.removeItem('aps-rocket-launched'); }
      if (wanted && MASCOTS[wanted]) window.localStorage.setItem('apw-mascot', wanted);
      var stored = window.localStorage.getItem('apw-mascot');
      if (stored && MASCOTS[stored]) this.mascot = stored;
    } catch (err) { /* storage blocked: default mascot */ }
  },
  mounted: function () {
    // Pilot users get this widget INSTEAD of the Voiceflow FAQ bubble (both sit bottom-right). The
    // Voiceflow host element lives in the light DOM, so a class on <html> is enough to hide it.
    document.documentElement.classList.add('apw-active');
    // First sight this browser session: wave and say hello once, then stay quiet.
    var seen = false;
    try { seen = !!window.sessionStorage.getItem('apw-greeted'); window.sessionStorage.setItem('apw-greeted', '1'); } catch (err) { /* storage blocked */ }
    if (!seen) {
      var that = this;
      window.setTimeout(function () { that.wave(); that.greeting = true; }, 900);
      this.greetTimer = window.setTimeout(function () { that.greeting = false; }, 9000);
    }
  },
  beforeUnmount: function () {
    document.documentElement.classList.remove('apw-active');
    window.clearTimeout(this.waveTimer);
    window.clearTimeout(this.greetTimer);
  },
  methods: {
    toggle: function () {
      this.open = !this.open;
      this.greeting = false;
      if (this.open && this.$refs.chat) {
        var chat = this.$refs.chat;
        this.$nextTick(function () { chat.launchRocketOnce(); });
      }
    },
    // Restart the wave animation (a class toggle so it can replay on every hover).
    wave: function () {
      if (this.open) return;
      var that = this;
      this.waving = false;
      window.clearTimeout(this.waveTimer);
      window.requestAnimationFrame(function () { that.waving = true; });
      this.waveTimer = window.setTimeout(function () { that.waving = false; }, 1400);
    },
  },
};
</script>

<style>
/* Hide the Voiceflow FAQ bubble while the widget is mounted (see mounted/beforeUnmount). */
html.apw-active #voiceflow-chat { display: none !important; }
</style>

<style scoped>
/* The mascot sits in the corner as a round button; open = smaller, with a cross. */
.apw-launcher { position: fixed; right: 20px; bottom: 16px; z-index: 2147482000;
  width: 84px; height: 84px; padding: 0; border: none; border-radius: 50%; cursor: pointer;
  background: #EEF1F7; box-shadow: 0 8px 22px rgba(30, 42, 120, 0.35);
  display: inline-flex; align-items: center; justify-content: center;
  transition: transform .18s, width .18s, height .18s, box-shadow .18s; }
.apw-launcher:hover { transform: translateY(-2px); box-shadow: 0 12px 26px rgba(30, 42, 120, 0.45); }
.apw-launcher:focus-visible { outline: 3px solid #00A76F; outline-offset: 2px; }
.apw-mascot { width: 74px; height: 74px; overflow: visible; }
.apw-launcher.apw-open { width: 48px; height: 48px; }
.apw-launcher.apw-open .apw-mascot { width: 40px; height: 40px; }
.apw-launcher.apw-open .apw-face { display: none; }
.apw-close { display: none; }
.apw-launcher.apw-open .apw-close { display: block; }

/* Idle blink every few seconds; a short wave when asked to. */
.apw-eyes { transform-origin: 48px 48px; animation: apw-blink 4.5s infinite; }
@keyframes apw-blink { 0%, 92%, 100% { transform: scaleY(1); } 95% { transform: scaleY(0.1); } }
.apw-hand { transform-origin: 80px 62px; }
.apw-wave .apw-hand { animation: apw-wave .35s ease-in-out 4 alternate; }
@keyframes apw-wave { from { transform: rotate(-18deg); } to { transform: rotate(22deg); } }

/* Greeting bubble to the left of the mascot. */
.apw-greeting { position: fixed; right: 116px; bottom: 38px; z-index: 2147482000; max-width: 260px;
  background: #fff; color: #1E2A78; font-family: 'Inter', system-ui, sans-serif; font-size: 13px; font-weight: 600;
  line-height: 1.35; padding: 10px 14px; border-radius: 14px 14px 4px 14px; cursor: pointer;
  box-shadow: 0 8px 22px rgba(30, 42, 120, 0.25); }
.apw-greeting::after { content: ''; position: absolute; right: -6px; bottom: 10px; border: 6px solid transparent; border-left-color: #fff; }

/* Tall panel anchored bottom-right, above the mascot. Its top clears the 80px app header (plus a
   margin) so the two never overlap; the height is whatever is left down to the launcher. */
.apw-panel { position: fixed; right: 20px; top: 104px; bottom: 108px; z-index: 2147482000;
  width: 520px; max-width: calc(100vw - 28px);
  background: #1c1c1e; border: 1px solid #2e2e34; border-radius: 18px; overflow: hidden;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.5); display: flex; flex-direction: column; }

.apw-fade-enter-active, .apw-fade-leave-active { transition: opacity .18s ease, transform .18s ease; }
.apw-fade-enter-from, .apw-fade-leave-to { opacity: 0; transform: translateY(8px); }

@media (prefers-reduced-motion: reduce) {
  .apw-eyes, .apw-wave .apw-hand { animation: none; }
}
</style>
